import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { BehaviorSubject } from 'rxjs';

import {
  InvalidAmountKey,
  IbanValidatorKey,
  NameValidatorKey,
  EmailValidatorKey,
  PhoneValidatorKey,
  SwiftValidatorKey,
  OtpInvalidLengthKey,
  AddressValidatorKey,
  OtpInvalidSymbolKey,
  InArrayValidatorKey,
  MaxDateValidatorKey,
  MinDateValidatorKey,
  SortCodeValidatorKey,
  PasswordValidatorKey,
  FileSizeValidatorKey,
  FileTypeValidatorKey,
  RequiredValidatorKey,
  MaxLengthValidatorKey,
  TaxNumberValidatorKey,
  OnlyLatinValidatorKey,
  MinLengthValidatorKey,
  WhitespaceValidatorKey,
  OnlyDigitsValidatorKey,
  LatinDigitsValidatorKey,
  EmailExistsValidatorKey,
  OnlyCyrillicValidatorKey,
  UnderEighteensValidatorKey,
  ConfirmPasswordValidatorKey,
  DuplicatedValueValidatorKey,
  BlackListedCountryValidatorKey
} from '@spa/validators';

import { Language } from '../../language';
import { BaseControl } from '../../classes';
import { ComponentTypesEnum } from '../../enums';
import { CountryValidationKey, InvalidSameAddressKey } from '../../providers';

export const InvalidAmountMaximumKey = 'invalidAmountMaximum';
export const DuplicatedIbanNumberKey = 'duplicatedIbanNumber';

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class FormComponent implements OnInit {
  public componentTypes: typeof ComponentTypesEnum;

  @Input() public form: FormGroup | undefined;
  @Input() public observable: BehaviorSubject<BaseControl<any>[]> | undefined;
  @Input() public hiddenActions: boolean | undefined;

  constructor() {
    this.componentTypes = ComponentTypesEnum;
  }

  private static formatControlLabel(isEnglish: boolean, label?: string): string {
    if (!label) {
      return isEnglish ? 'The field' : 'Полето';
    }

    label = label
      .replace(/\*/, '')
      .trim();

    return (label[0].toUpperCase() + label.slice(1).toLowerCase());
  }

  private static inArrayError(isEnglish: boolean, label?: string): string {
    if (!label) {
      return isEnglish ? 'Wrong value selected.' : 'Избрана е грешна стойност';
    }

    label = this.formatControlLabel(isEnglish, label);

    if (!isEnglish) {
      return `Wrong ${ label.toLowerCase() } selected.`;
    }

    return `Грешна ${ label.toLowerCase() } е избрана.`;
  }

  private static duplicatedText = (isEnglish: boolean, label?: string): string => {
    if (isEnglish) {
      return !label ? 'Value already selected.' : (FormComponent.formatControlLabel(isEnglish, label) + ' already selected.');
    }

    return !label ? 'Опцията е вече избрана.' : (FormComponent.formatControlLabel(isEnglish, label) + ' е вече избранa.');
  }

  private static passwordText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'The password must contain a minimum of 8 symbols, an uppercase letter, a lowercase letter, a special symbol, a number, and only Latin characters.';
    }

    return 'Паролата трябва да съдържа минимум 8 знака, главна буква, малка буква, символ и цифра';
  }

  private static fileSizeText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'The selected file is too large. Please upload an image that is less than 10MB.';
    }

    return 'Избраният файл е трърде голям. Моля добавете файл по - малък от 10MB.';
  }

  private static fileTypeText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'The selected file format is unrecognized. Please check the supported file types before uploading.';
    }

    return 'Грешен файл формат. Моля проверете поддържаните формати за ъплоад.';
  }

  private static maxLengthText(isEnglish: boolean, controlLabel: string, controlLength?: number): string {
    if (isEnglish) {
      return `${ this.formatControlLabel(isEnglish, controlLabel) } must be less than ${ controlLength } symbols.`;
    }

    return `${ this.formatControlLabel(isEnglish, controlLabel) } трябва да е по - малко от ${ controlLength } символа.`;
  }

  private static minLengthText(isEnglish: boolean, controlLabel: string, controlLength?: number): string {
    if (isEnglish) {
      return `${ this.formatControlLabel(isEnglish, controlLabel) } must be more than ${ controlLength } symbols.`;
    }

    return `${ this.formatControlLabel(isEnglish, controlLabel) } трябва да е повече от ${ controlLength } символа.`;
  }

  private static onlyLatinText(isEnglish: boolean, controlLabel?: string): string {
    if (isEnglish) {
      return `${ this.formatControlLabel(isEnglish, controlLabel) } must contain only Latin letters.`;
    }

    return `${ this.formatControlLabel(isEnglish, controlLabel) } трябва да съдържа само латински букви.`;
  }

  private static onlyDigitsText(isEnglish: boolean, controlLabel?: string): string {
    if (isEnglish) {
      return `${ this.formatControlLabel(isEnglish, controlLabel) } must contain only digits.`;
    }

    return `${ this.formatControlLabel(isEnglish, controlLabel) } трябва да съдържа само цифри.`;
  }

  private static onlyLatinDigitsAndLettersText(isEnglish: boolean, controlLabel?: string): string {
    if (isEnglish) {
      return `${ this.formatControlLabel(isEnglish, controlLabel) } must contain only Latin letters and digits.`;
    }

    return `${ this.formatControlLabel(isEnglish, controlLabel) } трябва да съдържа само латински букви и цифри.`;
  }

  private static onlyCyrillicLettersText(isEnglish: boolean, controlLabel?: string): string {
    if (isEnglish) {
      return `${ this.formatControlLabel(isEnglish, controlLabel) } must contain only Cyrillic letters.`;
    }

    return `${ this.formatControlLabel(isEnglish, controlLabel) } трябва да съдържа само букви на Кирилица.`;
  }

  private static blacklistedCountryText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'Selected nationality is marked as blacklisted. Client cannot be registered.';
    }

    return 'Избраната националност е маркирана в черен списък. Клиентът не може да бъде регистриран.';
  }

  private static invalidOtpCodeLengthText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'Invalid code. Confirmation code must contain six numbers.';
    }

    return 'Невалиден код. Кодът за потвърждение трябва да съдържа шест цифри.';
  }

  private static invalidOtpCodeSymbolText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'Invalid code. Confirmation code must contain only numbers.';
    }

    return 'Невалиден код. Кодът за потвърждение трябва да съдържа само цифри.';
  }

  private static addressText(isEnglish: boolean, controlLabel?: string): string {
    if (isEnglish) {
      return `${ this.formatControlLabel(isEnglish, controlLabel) } must be in Latin.`;
    }

    return `${ this.formatControlLabel(isEnglish, controlLabel) } трябва да съдържа латински букви и цифри.`;
  }

  private static passwordMatchText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'Your password and confirmation password do not match.';
    }

    return 'Вашата парола и паролата за потвърждение не съвпадат.';
  }

  private static sameAddressText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'Please provide a different mailing address or mark the residential address as mailing';
    }

    return 'Моля представете различен адрес за кореспонденция или отбележете настоящият адрес като адрес за кореспонденция';
  }

  private static taxNumberText(isEnglish: boolean): string {
    if (isEnglish) {
      return 'Invalid tax number. Please double-check that the number entered contains only numbers, upper and lower case letters and/or the symbols “.“, “-“, “/“';
    }

    return 'Невалиден данъчен номер. Моля проверете отново дали номера съдържа само цифри, главни и малки букви и/или символите “.“, “-“, “/“';
  }

  private static nameText(isEnglish: boolean, controlLabel?: string): string {
    if (isEnglish) {
      return `${ this.formatControlLabel(isEnglish, controlLabel) } must contain only Latin letters and/or the symbols “-“, “'“, “.“`;
    }

    return `${ this.formatControlLabel(isEnglish, controlLabel) } трябва да съдържа само латински букви и/или символите “-“, “'“, “.“`;
  }

  static getFormMessages(
    currentLanguage: string,
    controlLabel?: string,
    controlLength?: number,
  ): { [key: string]: string; } {
    const isEnglish = currentLanguage === Language.English;
    return {
      [DuplicatedIbanNumberKey]: isEnglish ? 'This IBAN is already associated with your client\'s profile' : 'Този IBAN е вече асоцииран с клиентския Ви профил.',
      [RequiredValidatorKey]: isEnglish ? '* required field.' : '* задължително поле',
      [WhitespaceValidatorKey]: isEnglish ? '* required field.' : '* задължително поле',
      [InArrayValidatorKey]: this.inArrayError(isEnglish, controlLabel),
      [PhoneValidatorKey]: isEnglish ? 'The selected phone number is incorrect.' : 'Избраният телефонен номер е грешен.',
      [EmailValidatorKey]: isEnglish ? 'Email address is invalid.' : 'Имейл адресът е невалиден.',
      [UnderEighteensValidatorKey]: isEnglish ? 'You must be at least 18 years old to register an account.' : 'Минималната възраст за регистрация на клиент е 18 години.',
      [PasswordValidatorKey]: this.passwordText(isEnglish),
      [DuplicatedValueValidatorKey]: this.duplicatedText(isEnglish, controlLabel),
      [MinDateValidatorKey]: isEnglish ? 'The date must be greater than selected.' : 'Датата трябва да е по - голяма от избраната',
      [MaxDateValidatorKey]: isEnglish ? 'The date must be less than selected.' : 'Датата трябва да е по - малка от избраната',
      [FileSizeValidatorKey]: this.fileSizeText(isEnglish),
      [FileTypeValidatorKey]: this.fileTypeText(isEnglish),
      [MaxLengthValidatorKey]: this.maxLengthText(isEnglish, controlLabel ?? '', controlLength),
      [MinLengthValidatorKey]: this.minLengthText(isEnglish, controlLabel ?? '', controlLength),
      [OnlyLatinValidatorKey]: this.onlyLatinText(isEnglish, controlLabel),
      [OnlyDigitsValidatorKey]: this.onlyDigitsText(isEnglish, controlLabel),
      [LatinDigitsValidatorKey]: this.onlyLatinDigitsAndLettersText(isEnglish, controlLabel),
      [IbanValidatorKey]: isEnglish ? 'IBAN is invalid.' : 'IBAN е невалиден.',
      [SwiftValidatorKey]: isEnglish ? 'BIC/SWIFT is invalid.' : 'BIC/SWIFT е невалиден.',
      [OnlyCyrillicValidatorKey]: this.onlyCyrillicLettersText(isEnglish, controlLabel),
      [EmailExistsValidatorKey]: isEnglish ? 'The selected email address already exists.' : 'Избраният имейл адрес вече съществува.',
      [BlackListedCountryValidatorKey]: this.blacklistedCountryText(isEnglish),
      [OtpInvalidLengthKey]: this.invalidOtpCodeLengthText(isEnglish),
      [OtpInvalidSymbolKey]: this.invalidOtpCodeSymbolText(isEnglish),
      [InvalidAmountKey]: isEnglish ? 'Amount must be greater than 0.00.' : 'Сумата трябва да е по - голяма от 0.00.',
      [InvalidAmountMaximumKey]: isEnglish ? 'Max amount exceeded.' : 'Надвишена максимална стойност.',
      [AddressValidatorKey]: this.addressText(isEnglish, controlLabel),

      // This is only in UK so doesn't need translation
      [SortCodeValidatorKey]: 'Invalid sort code. The code should contain three couple from digits separated with dash(xx-xx-xx).',

      [ConfirmPasswordValidatorKey]: this.passwordMatchText(isEnglish),
      [InvalidSameAddressKey]: this.sameAddressText(isEnglish),
      [TaxNumberValidatorKey]: this.taxNumberText(isEnglish),
      [NameValidatorKey]: this.nameText(isEnglish, controlLabel),
      [CountryValidationKey]: isEnglish ? 'We do not offer service in your country' : 'Услугата е недостъпна във Вашата страна'
    };
  }

  ngOnInit(): void {
    if (!this.form) {
      this.form = new FormGroup({});
    }

    if (!this.observable) {
      this.observable = new BehaviorSubject<BaseControl[]>([]);
    }

    if (typeof this.hiddenActions === 'undefined') {
      this.hiddenActions = false;
    }
  }

  removeControl(index: number): void {
    if (this.observable) {
      const removedControls = this.observable.value.splice(index, 1);

      if (removedControls.length > 0) {
        const name = removedControls[0].name;

        if (this.form) {
          this.form.removeControl(name);
        }
      }
    }
  }
}
